import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Shared page grammar taken from the Stitch frames: 24px title, 14px muted subtitle, actions right-aligned.
export function PageHeader({ title, subtitle, actions, chip, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">{title}</h1>
          {chip}
        </div>
        {subtitle && <p className="mt-1 text-sm text-jigsaw-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
  );
}

export function Breadcrumbs({ items, testId }) {
  return (
    <nav data-testid={testId} aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-[12px] text-jigsaw-muted">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          {it.to ? <Link to={it.to} className="hover:text-jigsaw-navy hover:underline">{it.label}</Link> : <span className={cn(i === items.length - 1 && "font-medium text-jigsaw-navy")}>{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

// Detail-screen header: breadcrumb, reference + status chips, title, uppercase meta grid, action rail (reference case-file grammar).
export function DetailHeader({ crumbs, reference, chips, title, meta = [], actions, testId, titleTestId }) {
  return (
    <section data-testid={testId} className="rounded-[10px] border border-jigsaw-border bg-white px-5 pb-4 pt-4">
      {crumbs && <Breadcrumbs items={crumbs} />}
      <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {reference && <span className="font-mono-ref rounded-md bg-jigsaw-navy px-2 py-0.5 text-[12px] font-semibold text-white">{reference}</span>}
            {chips}
          </div>
          <h1 data-testid={titleTestId} className="mt-2 text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">{title}</h1>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 lg:shrink-0">{actions}</div>}
      </div>
      {meta.length > 0 && <MetaGrid items={meta} className="mt-4 border-t border-jigsaw-border pt-4" />}
    </section>
  );
}

export function MetaGrid({ items, className, cols = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" }) {
  return (
    <dl className={cn("grid gap-x-6 gap-y-3", cols, className)}>
      {items.filter(Boolean).map((m) => (
        <div key={m.label} className={cn("min-w-0", m.span && "col-span-2")}>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">{m.label}</dt>
          <dd className={cn("mt-0.5 break-words text-[14px] text-jigsaw-navy", m.wrap ? "whitespace-normal [overflow-wrap:anywhere]" : "sm:truncate", m.mono && "font-mono-ref", m.className)} title={typeof m.value === "string" ? m.value : undefined}>{m.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionCard({ title, subtitle, action, children, testId, className, bodyClassName }) {
  return (
    <section data-testid={testId} className={cn("min-w-0 max-w-full rounded-[10px] border border-jigsaw-border bg-white", className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
          <div><h2 className="text-[16px] font-semibold text-jigsaw-navy">{title}</h2>{subtitle && <p className="mt-0.5 text-[13px] text-jigsaw-muted">{subtitle}</p>}</div>
          {action}
        </header>
      )}
      <div className={cn("px-5 pb-5", !title && "pt-5", bodyClassName)}>{children}</div>
    </section>
  );
}

// Summary tile row used under detail headers (reference "placement status / vault integrity" tiles), values from real data only.
export function SummaryTile({ label, value, sub, tone = "default", testId }) {
  const tones = { default: "text-jigsaw-navy", success: "text-jigsaw-success", warning: "text-jigsaw-warning", danger: "text-jigsaw-danger" };
  return (
    <div data-testid={testId} className="rounded-[10px] border border-jigsaw-border bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">{label}</p>
      <p className={cn("mt-1 text-[24px] font-bold leading-none tracking-[-0.02em]", tones[tone])}>{value}</p>
      {sub && <p className="mt-1 text-[12px] text-jigsaw-muted">{sub}</p>}
    </div>
  );
}

export function FilterBar({ children, summary, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3 rounded-[10px] border border-jigsaw-border bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-2">{children}</div>
      {summary && <p className="text-[12px] text-jigsaw-muted lg:shrink-0">{summary}</p>}
    </div>
  );
}
